import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Lock } from "lucide-react";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_SPOTS = 500;

const scrollTo = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const CTA = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [claimed, setClaimed] = useState(null);

  useEffect(() => {
    const load = () =>
      axios
        .get(`${API}/stats/founding`)
        .then((res) => setClaimed(res.data.total))
        .catch(() => setClaimed(null));
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { email });
      setDone(true);
      toast.success("You're on the founding list");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="access" data-testid="cta-section" className="relative py-32 overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 grid-lines" />
      <div className="absolute inset-0 hero-spotlight" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeUp>
          <span className="inline-flex items-center gap-2 border border-volt/40 bg-volt/10 px-4 py-2 font-mono text-[11px] tracking-widest uppercase text-volt">
            <Lock className="h-3.5 w-3.5" />
            Limited to 500 founding members
          </span>
          {claimed !== null && (
            <div data-testid="waitlist-counter" className="mt-8 mx-auto max-w-md">
              <div className="flex justify-between font-mono text-[11px] tracking-widest uppercase mb-2">
                <span className="text-neutral-500">{claimed} of {TOTAL_SPOTS} founding spots claimed</span>
                <span className="text-volt">{Math.max(TOTAL_SPOTS - claimed, 0)} left</span>
              </div>
              <div data-testid="waitlist-progress-bar" className="h-1.5 bg-white/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${Math.min((claimed / TOTAL_SPOTS) * 100, 100)}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full bg-volt"
                />
              </div>
            </div>
          )}
          <h2 className="font-display font-black uppercase tracking-tight leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mt-8">
            Your First Workout Takes 10 Minutes.<br />
            <span className="text-volt italic">Your Next 12 Months Are On Us.</span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-neutral-400 leading-relaxed">
            Join as a founding member and get full access to every workout duration, the
            complete exercise library, high-protein meal recipes, live smartwatch coaching,
            and offline downloads — from the moment the app opens.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          {done ? (
            <div
              data-testid="email-capture-success"
              className="mt-10 mx-auto max-w-lg border border-volt/50 bg-volt/10 p-8 flex items-center justify-center gap-4"
            >
              <CheckCircle2 className="h-8 w-8 text-volt shrink-0" />
              <div className="text-left">
                <p className="font-display font-bold uppercase text-xl text-volt">You&rsquo;re in.</p>
                <p className="text-sm text-neutral-400 mt-1">Watch your inbox — onboarding details arrive before doors open.</p>
              </div>
            </div>
          ) : (
            <form
              data-testid="email-capture-form"
              onSubmit={submit}
              className="mt-10 mx-auto max-w-lg flex flex-col sm:flex-row gap-3"
            >
              <input
                data-testid="email-capture-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address..."
                className="flex-1 bg-carbon border border-white/15 px-5 py-4 text-base text-neutral-100 placeholder:text-[#888888] focus:outline-none focus:border-volt transition-colors duration-200"
              />
              <button
                data-testid="email-capture-submit-button"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-2 bg-volt text-white font-display font-bold uppercase tracking-wide px-8 py-4 hover:bg-volt-hover hover:shadow-[0_0_35px_rgba(255,107,0,0.5)] disabled:opacity-60 transition-[background-color,box-shadow,opacity] duration-200"
              >
                {loading ? "Joining..." : "Claim Your Spot"}
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </form>
          )}

          <div className="mt-10 flex items-center gap-4 max-w-lg mx-auto">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">or skip the waitlist</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button
            data-testid="cta-founding-pricing-button"
            onClick={() => scrollTo("#pricing")}
            className="mt-6 inline-flex items-center gap-3 bg-volt text-white font-display font-bold uppercase tracking-wide text-lg px-8 py-4 hover:bg-volt-hover hover:shadow-[0_0_35px_rgba(255,107,0,0.5)] transition-[background-color,box-shadow] duration-200"
          >
            Lock In Founding Pricing Now →
          </button>

          <div data-testid="app-store-badges" className="mt-10">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">Coming soon to</p>
            <div className="mt-4 flex items-center justify-center gap-4">
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on the App Store — coming soon"
                className="h-10 opacity-50"
              />
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play — coming soon"
                className="h-14 opacity-50"
              />
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
